import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/constants";
import { PageSEO } from "@/components/PageSEO";
import { CheckCircle } from "lucide-react";

const FIELDS = [
  { k: "nome", l: "Nome e cognome", type: "text", req: true },
  { k: "codice_fiscale", l: "Codice fiscale", type: "text", req: true },
  { k: "partita_iva", l: "Partita IVA", type: "text", req: true },
  { k: "data_apertura", l: "Data apertura partita IVA", type: "date", req: true },
  { k: "codice_ateco", l: "Codice ATECO", type: "text", req: true },
  { k: "fatturato_previsto", l: "Fatturato previsto (indicativo)", type: "text", req: false },
  { k: "indirizzo", l: "Indirizzo completo", type: "text", req: true },
  { k: "email", l: "Email", type: "email", req: true },
  { k: "telefono", l: "Telefono", type: "tel", req: true },
  { k: "iban", l: "IBAN per l'addebito degli F24", type: "text", req: true, wide: true, note: "Su questo conto verranno addebitati gli F24. Per modificarlo in seguito è necessario inviare una email allo studio." }
];

const inputCls = "w-full bg-white border border-[#D6CFC0] focus:border-gold rounded px-4 py-3 text-[#0D1527] placeholder-[#9AA3B2] outline-none transition-colors";

export default function ForfettariOnboardingPage() {
  const [form, setForm] = useState({ gestione_previdenziale: "", riduzione_contributiva: "", imposta_5: "", note: "" });
  const [privacy, setPrivacy] = useState(false);
  const [status, setStatus] = useState("idle");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    try {
      await axios.post(`${API}/forfettari/onboarding`, { ...form, privacy_accepted: privacy });
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  return (
    <main data-testid="forfettari-onboarding-page" className="bg-[#FAF8F5] pt-32 pb-24 min-h-screen">
      <PageSEO title="Attivazione Contabilità Forfettari | SC Dottori Commercialisti" description="Modulo di attivazione del servizio di contabilità per forfettari." noindex />
      <div className="max-w-2xl mx-auto px-6">
        <p className="text-gold font-bold tracking-[0.3em] text-sm mb-4">CONTABILITÀ FORFETTARI</p>
        <h1 className="font-heading text-4xl md:text-5xl text-[#0D1527] mb-4">BENVENUTO! IMPOSTIAMO LA TUA CONTABILITÀ</h1>
        <p className="text-[#4A5568] mb-10">Il pagamento è andato a buon fine. Questi dati ci servono per impostare correttamente la contabilità e la tua posizione previdenziale. Richiede 3 minuti.</p>

        {status === "done" ? (
          <div className="bg-[#F7F0E1] border border-gold/40 rounded-lg p-10 text-center" data-testid="onboarding-success">
            <CheckCircle className="text-gold mx-auto mb-4" size={40} />
            <h2 className="font-heading text-3xl text-[#0D1527] mb-3">DATI RICEVUTI, GRAZIE!</h2>
            <p className="text-[#4A5568] mb-2">Impostiamo la tua posizione e ti scriviamo entro 2 giorni lavorativi con le credenziali del portale.</p>
            <p className="text-[#64708A] text-sm mb-2">Ricorda: se usi il nostro portale di fatturazione non devi caricare gli XML delle fatture emesse (le vediamo direttamente); altrimenti li carichi su <span className="text-gold">app.casalisilvia.com</span>.</p>
            <p className="text-[#64708A] text-sm">A gennaio nell'app carichi le date di incasso e i contributi previdenziali versati l'anno precedente: gli F24 se hai l'INPS, oppure il documento della tua cassa privata con il totale versato. Completato il caricamento, entro 1 mese ricevi una email con l'esito e il dettaglio dell'F24.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="onboarding-form">
            <div className="grid sm:grid-cols-2 gap-5">
              {FIELDS.map(f => (
                <div key={f.k} className={f.k === "indirizzo" || f.wide ? "sm:col-span-2" : ""}>
                  <label className="block text-[#4A5568] text-sm mb-2">{f.l}{f.req && " *"}</label>
                  <input type={f.type} required={f.req} value={form[f.k] || ""} onChange={e => set(f.k, e.target.value)} className={inputCls} data-testid={`onb-${f.k}`} />
                  {f.note && <p className="text-[#8B94A6] text-xs mt-2">{f.note}</p>}
                </div>
              ))}
              <div>
                <label className="block text-[#4A5568] text-sm mb-2">Gestione previdenziale *</label>
                <select required value={form.gestione_previdenziale} onChange={e => set("gestione_previdenziale", e.target.value)} className={inputCls} data-testid="onb-gestione">
                  <option value="">Seleziona...</option>
                  <option>Artigiani INPS</option>
                  <option>Commercianti INPS</option>
                  <option>Gestione Separata INPS</option>
                  <option>Cassa professionale</option>
                  <option>Non lo so</option>
                </select>
              </div>
              <div>
                <label className="block text-[#4A5568] text-sm mb-2">Riduzione contributiva attiva?</label>
                <select value={form.riduzione_contributiva} onChange={e => set("riduzione_contributiva", e.target.value)} className={inputCls} data-testid="onb-riduzione">
                  <option value="">Seleziona...</option>
                  <option>Sì</option>
                  <option>No</option>
                  <option>Non lo so</option>
                </select>
              </div>
              <div>
                <label className="block text-[#4A5568] text-sm mb-2">Imposta sostitutiva 5% (nuova attività)?</label>
                <select value={form.imposta_5} onChange={e => set("imposta_5", e.target.value)} className={inputCls} data-testid="onb-imposta5">
                  <option value="">Seleziona...</option>
                  <option>Sì</option>
                  <option>No</option>
                  <option>Non lo so</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[#4A5568] text-sm mb-2">Note (facoltative)</label>
              <textarea rows={3} value={form.note} onChange={e => set("note", e.target.value)} className={inputCls} data-testid="onb-note" />
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" required checked={privacy} onChange={e => setPrivacy(e.target.checked)} className="mt-1 w-4 h-4 accent-[#c8a96e]" data-testid="onb-privacy" />
              <span className="text-[#64708A] text-xs leading-relaxed">
                Ho letto l'<Link to="/privacy-policy" className="underline text-[#4A5568]">informativa privacy</Link> e acconsento al trattamento dei dati per l'erogazione del servizio di contabilità. *
              </span>
            </label>
            <button type="submit" disabled={status === "loading" || !privacy} className="w-full bg-gold text-black hover:bg-[#96742F] px-8 py-4 rounded font-bold tracking-wide transition-all disabled:opacity-50" data-testid="onb-submit">
              {status === "loading" ? "INVIO..." : "INVIA I DATI DI ATTIVAZIONE"}
            </button>
            {status === "error" && <p className="text-red-400 text-sm" data-testid="onb-error">Si è verificato un errore. Riprova tra qualche istante.</p>}
          </form>
        )}
      </div>
    </main>
  );
}
